import React from 'react';
import {
  Stack,
  Text,
  Input,
  Button,
  Grid,
  ButtonGroup,
  Select,
  Shelf,
  Token,
} from '@chaser/components';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import CardSection from '../../../../components/CardSection/index';
import useOrganisation from '../../../../hooks/useOrganisation';
import usePush from '../../../../hooks/usePush';
import SettingsService from '../../../../services/settings-service';
import timezones from '../../../../util/timezones';
import { useCacheActions } from '../../../../hooks/useFetch/index';
import OrganisationService from '../../../../services/organisation-service';
import AccountService from '../../../../services/account-service';
import FieldWithErrorLabel from '../../../../components/FieldWithErrorLabel';
import { IntegrationDisplayNames, Integrations } from '../../../../util/constants';
import { getAddressLine } from '../../../../util/address';
import countries from '../../../addorganisation/welcome/countries';
import LabeledFieldWithErrorLabel from '../../../../components/LabeledFieldWithErrorLabel';

const AddressForm = () => {
  const { execute, loading } = usePush(SettingsService.setOrganisationAddress, {
    successToast: 'Organisation address changed',
    errorToast: true,
  });
  const { invalidateEndpoint } = useCacheActions();

  const { accountingSoftware, Addresses, ShortCode } = useOrganisation();

  const controlledByChaser = [Integrations.OPENAPI, Integrations.ZAPIER, Integrations.CSV];
  const isControlledByChaser = !controlledByChaser.includes(accountingSoftware);

  const addressLine2 = getAddressLine(Addresses, 'AddressLine2') || '';
  const addressLine3 = getAddressLine(Addresses, 'AddressLine3') || '';
  const addressLine4 = getAddressLine(Addresses, 'AddressLine4') || '';

  const address = getAddressLine(Addresses, 'AddressLine1');
  const floor = `${addressLine2} ${addressLine3} ${addressLine4}`.trim();
  const city = getAddressLine(Addresses, 'City');
  const region = getAddressLine(Addresses, 'Region');
  const postal = getAddressLine(Addresses, 'PostalCode');
  const country = getAddressLine(Addresses, 'Country') || '';

  return (
    <Formik
      initialValues={{
        address,
        floor,
        city,
        region,
        postal,
        country,
      }}
      isInitialValid={false}
      enableReinitialize
      validateOnChange
      validateOnBlur
      validateOnMount
      validationSchema={Yup.object({
        address: Yup.string().required('Address is required'),
        postal: Yup.string().required('Postal code is required'),
        country: Yup.string().required('Country is required'),
      })}
      onSubmit={async ({ address, floor, city, postal, region, country }, { setSubmitting }) => {
        const [err] = await execute({
          address,
          floor,
          city,
          postal,
          country,
          region,
          shortCode: ShortCode,
        });
        setSubmitting(false);
        if (err) return;
        invalidateEndpoint(OrganisationService.get);
      }}
    >
      {({ dirty }) => (
        <Form>
          <Stack gap="medium">
            <Stack gap="small">
              <Text>This address will be included on letters sent using Chaser</Text>
            </Stack>
            <Grid
              columns={2}
              rowGap="small"
              alignItems="center"
              gridTemplateColumns="200px auto"
              width="100%"
            >
              <Text color="Mid Neutral 1">Address</Text>
              <LabeledFieldWithErrorLabel
                disabled={isControlledByChaser}
                name="address"
                as={Input}
                type="input"
              />
              <Text color="Mid Neutral 1">Apartment, flat or unit number</Text>
              <Field disabled={isControlledByChaser} name="floor" as={Input} type="input" />
              <Text color="Mid Neutral 1">City</Text>
              <Field disabled={isControlledByChaser} name="city" as={Input} type="input" />
              <Text color="Mid Neutral 1">County / State</Text>
              <Field disabled={isControlledByChaser} name="region" as={Input} type="input" />
              <Text color="Mid Neutral 1">Postal code / Zip code</Text>
              <LabeledFieldWithErrorLabel
                disabled={isControlledByChaser}
                name="postal"
                as={Input}
                type="input"
              />
              <Text color="Mid Neutral 1">Country</Text>
              {isControlledByChaser ? (
                <Field disabled={isControlledByChaser} name="country" as={Input} type="input" />
              ) : (
                <LabeledFieldWithErrorLabel
                  disabled={isControlledByChaser}
                  name="country"
                  width="300px"
                  as={Select}
                >
                  <option value="" disabled>
                    Select a country
                  </option>
                  {countries.map(c => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </LabeledFieldWithErrorLabel>
              )}
            </Grid>
            {!isControlledByChaser ? (
              <ButtonGroup>
                <Button
                  disabled={!dirty || loading}
                  loading={loading}
                  type="submit"
                  appearance="secondary"
                >
                  Save address
                </Button>
              </ButtonGroup>
            ) : (
              <Text color="Mid Neutral 1">
                Your address is managed in {IntegrationDisplayNames[accountingSoftware]}
              </Text>
            )}
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

const NameForm = () => {
  const { execute, loading } = usePush(SettingsService.setOrganisationName, {
    successToast: 'Organisation name changed',
    errorToast: true,
  });

  const { name, ShortCode } = useOrganisation();
  const { invalidateEndpoint } = useCacheActions();

  return (
    <Formik
      initialValues={{
        organisationName: name,
      }}
      validationSchema={Yup.object({
        organisationName: Yup.string()
          .max(50, 'Must be 50 characters or less')
          .min(3, 'Must be more than 3 characters')
          .required('This cannot be empty!'),
      })}
      onSubmit={async ({ organisationName }, { setSubmitting }) => {
        const [err] = await execute(organisationName, ShortCode);
        setSubmitting(false);
        if (err) return;
        invalidateEndpoint(OrganisationService.get);
        invalidateEndpoint(AccountService.getUserOrganisations);
      }}
    >
      {({ values }) => (
        <Form>
          <Stack>
            <Text>How your organisation appears in Chaser, including chasing emails</Text>
            <FieldWithErrorLabel
              name="organisationName"
              as={Input}
              placeholder="Enter new name"
              type="input"
            />
            <div>
              <Button
                disabled={!values.organisationName || !(values.organisationName !== name)}
                loading={loading}
                appearance="secondary"
                type="submit"
              >
                Save name
              </Button>
            </div>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

const TimezoneForm = () => {
  const { execute, loading } = usePush(SettingsService.setOrganisationTimezone, {
    successToast: 'Organisation timezone updated',
  });

  const { timezone, ShortCode } = useOrganisation();
  const { invalidateEndpoint } = useCacheActions();

  return (
    <Formik
      initialValues={{
        timezone,
      }}
      onSubmit={async ({ timezone }, { setSubmitting }) => {
        const [err] = await execute(timezone, ShortCode);
        setSubmitting(false);
        if (err) return;
        invalidateEndpoint(OrganisationService.get);
      }}
    >
      {({ values }) => (
        <Form>
          <Stack>
            <Text>This determines what timezone your chases are being sent in</Text>
            <FieldWithErrorLabel label="Timezone" name="timezone" width="300px" as={Select}>
              {timezones.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </FieldWithErrorLabel>
            <div>
              <Button
                disabled={!values.timezone || !(values.timezone !== timezone)}
                loading={loading}
                appearance="secondary"
                type="submit"
              >
                Save timezone
              </Button>
            </div>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

const SubsidiariesForm = () => {
  const { Subsidiaries } = useOrganisation();

  const enabledSubsidiaryNames = Subsidiaries?.filter(({ enabled }) => enabled).map(
    ({ name }) => name,
  );

  return (
    <Stack>
      <Text>Data retrieval will be performed from the following subsidiaries</Text>
      <Shelf>
        {enabledSubsidiaryNames.map(subsidiary => (
          <Token>{subsidiary}</Token>
        ))}
      </Shelf>
    </Stack>
  );
};

const Organisation = () => {
  const { accountingSoftware } = useOrganisation();

  return (
    <Stack>
      <CardSection title="Organisation name">
        <NameForm />
      </CardSection>

      {accountingSoftware === Integrations.NETSUITE && (
        <CardSection title="Subsidiaries">
          <SubsidiariesForm />
        </CardSection>
      )}

      <CardSection title="Address">
        <AddressForm />
      </CardSection>

      <CardSection title="Timezone">
        <TimezoneForm />
      </CardSection>
    </Stack>
  );
};

export default Organisation;
